<script>
import RestrictedSocialBookingDatesService from '@/services/RestrictedSocialBookingDatesService';
import RestrictedSocialDateModal from '@/views/restrictedSocialBookingDates/modals/RestrictedSocialDateModal.vue';
import VisitLocationsService from '@/services/VisitLocationsService';
import { mapState } from 'vuex';

export default {
  name: 'RestrictedSocialBookingDates',
  components: { RestrictedSocialDateModal },
  data() {
    return {
      loading: true,
      visitLocations: [],
      restrictedBookingDates: [],
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      fields: [
        { label: 'Location', key: 'locationId', thStyle: { width: '20%' } },
        { label: 'Name', key: 'name', thStyle: { width: '20%' } },
        { label: 'New Table Count', key: 'newTableCount', thStyle: { width: '20%' } },
        { label: 'Start date', key: 'startDate', thStyle: { width: '15%' } },
        { label: 'End date', key: 'endDate', thStyle: { width: '15%' } },
        { key: 'actions', thStyle: { width: '10%' } },
      ],
      filters: {
        name: '',
        sort: 'start_date',
      },
      sorts: [
        { text: 'Upcoming Start', value: 'start_date' },
        { text: 'Furthest Start', value: '-start_date' },
        { text: 'Upcoming End', value: 'end_date' },
        { text: 'Furthest end', value: '-end_date' },
      ],
      restrictedDateModalId: 'restricted-date-modal-id',
      selectedDate: null,
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
  },
  async mounted() {
    try {
      await this.getVisitLocations();
      await this.getRestrictedSocialDates();
      await this.$store.dispatch('prison/getPrison');
      this.loading = false;
    } catch (e) {
      this.$router.go(-1);
    }
  },
  methods: {
    async getVisitLocations() {
      try {
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations: ', e);
        this.$toast.error('Unable to get visit locations. Please refresh the page and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getRestrictedSocialDates() {
      try {
        const response = await RestrictedSocialBookingDatesService.getRestrictedSocialDates({
            ...this.filters,
            skip: (this.currentPage - 1) * this.perPage,
            limit: this.perPage,
        });
        this.restrictedBookingDates = response.data.data;
        this.totalCount = response.data.count;
      } catch (e) {
        console.error('Error getting restricted social booking dates: ', e);
        this.$toast.error('Error getting restricted social booking dates', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    closeModals() {
      this.selectedDate = null;
      this.$bvModal.hide(this.restrictedDateModalId);
    },
    refresh() {
      this.selectedDate = null;
      this.closeModals();
      this.getRestrictedSocialDates();
    },
    showModal(date) {
      this.selectedDate = date;
      this.$bvModal.show(this.restrictedDateModalId);
    },
    async deleteDate(date) {
      try {
        this.loading = true;
        const proceed = await this.$swal({
          title: 'Are you sure?',
          text: `Are you sure you want to delete date ${date.name}?`,
          icon: 'info',
          buttons: true,
        });
        if (!proceed) return;
        await RestrictedSocialBookingDatesService.deleteDate(date._id);
        await this.getRestrictedSocialDates();
        this.$toast.success('Restricted date deleted', {
          toastClassName: ['toast-std', 'success-toast'],
        });
      } catch (e) {
        console.error('Error deleting restricted date: ', e);
        this.$toast.error('Error deleting restricted date', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.loading = false;
      }
    },
    debounce(func, timeout = 300) {
      return (...args) => {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    search() {
      this.currentPage = 1;
      this.debounce(this.getRestrictedSocialDates)();
    },
    locationName(locationId) {
      const matchingLocation = this.visitLocations.find(l => l._id === locationId);
      return matchingLocation ? matchingLocation.name : 'Unknown';
    },
  },
}
</script>

<template>
  <div>
    <b-card>
      <h2>Restricted Social Booking Dates</h2>
      <b-btn
        class="float-right"
        variant="primary"
        :disabled="loading"
        @click="showModal()"
      >
        Add Date
      </b-btn>
    </b-card>
    <b-card no-body>
      <b-row class="p-2">
        <b-col cols="3">
          <label>
            Name
          </label>
          <b-input-group>
            <b-form-input
              v-model="filters.name"
              class="d-inline-block"
              placeholder="Enter a date's name..."
              @keydown.enter="search"
              @input="search"
            />
          </b-input-group>
        </b-col>
        <b-col cols="3">
          <label>Sort</label>
          <select v-model="filters.sort" class="form-control" @change="getRestrictedSocialDates">
            <option v-for="sort in sorts" :key="sort.value" :value="sort.value">{{ sort.text }}</option>
          </select>
        </b-col>
      </b-row>
      <b-table
        :current-page="currentPage"
        :fields="fields"
        :items="restrictedBookingDates"
        class="mb-0"
        hover
        responsive
        show-empty
      >
        <template #cell(locationId)="data">
          {{ locationName(data.item.visit_location_id) }}
        </template>
        <template #cell(name)="data">
          {{ data.item.name }}
        </template>
        <template #cell(newTableCount)="data">
          {{ data.item.social_table_count || 'N/A' }}
        </template>
        <template #cell(startDate)="data">
          {{ data.item.start_date| formatDate }}
        </template>
        <template #cell(endDate)="data">
          {{ data.item.end_date | formatDate }}
        </template>
        <template #cell(actions)="data">
          <b-dropdown class="m-0" no-caret right toggle-class="text-decoration-none" variant="link">
            <template #button-content>
              <i class="fas fa-ellipsis-v" />
            </template>
            <b-dropdown-item-button
              class="full-nested-btn"
              :disabled="loading"
              @click="showModal(data.item)"
            >
              Update
            </b-dropdown-item-button>
            <b-dropdown-item-button
              class="full-nested-btn"
              :disabled="loading"
              @click="deleteDate(data.item)"
            >
              Delete
            </b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
      <b-row v-if="restrictedBookingDates && restrictedBookingDates.length">
        <b-col class="m-1">
          <b-pagination
            v-model="currentPage"
            :per-page="perPage"
            :total-rows="totalCount"
            class="my-0"
            @input="getRestrictedSocialDates"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal :id="restrictedDateModalId" :title="!selectedDate ? 'Create Restricted Date' : 'Update Restricted Date'" hide-footer>
      <restricted-social-date-modal
        :visit-locations="visitLocations"
        :existing="selectedDate"
        @close="closeModals"
        @refresh="refresh"
      />
    </b-modal>
  </div>
</template>
